import React from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { Display, Box } from '@gousto-internal/citrus-react'
import { Form, FormikProvider } from 'formik'
import { useSelector } from 'react-redux'

import type { PaymentProps } from 'routes/Checkout/Steps/Payment/Payment'
import { getDeliveryAddress, getEmail } from 'routes/Checkout/checkoutSelectors'

import { ErrorMessage } from '../../ErrorMessage/ErrorMessage'
import { SubmitButton } from '../../SubmitButton'
import { CheckoutBillingAddress } from '../CheckoutBillingAddress'
import { CheckoutFrame } from '../CheckoutFrame/CheckoutFrame'
import { useAddressDetailsForm } from '../useAddressDetailsForm'
import { useHostedFields } from './useHostedFields'

type CheckoutPaymentDetailsProps = Pick<
  PaymentProps,
  'submitOrder' | 'onLoginClick' | 'braintreeClientInstance' | 'threeDSecureInstance'
> & {
  hide: boolean
}

export const HostedFields = ({
  submitOrder,
  onLoginClick,
  hide,
  braintreeClientInstance,
  threeDSecureInstance,
}: CheckoutPaymentDetailsProps) => {
  const formik = useAddressDetailsForm()
  const deliveryAddress = useSelector(getDeliveryAddress)
  const email = useSelector(getEmail)

  const {
    formValidationError,
    isFormValid,
    handleCardDetailsValidation,
    handleSubmit,
    isFormSubmitting,
    setHostedFieldsFrameLoaded,
  } = useHostedFields({
    braintreeClientInstance,
    submitOrder,
    threeDSecureInstance,
  })

  const processSubmit = () => {
    if (!isFormValid || !formik.values.cardHolderName) {
      handleCardDetailsValidation()
      formik.setFieldTouched('cardHolderName')
    }
    if (isFormValid && formik.isValid) {
      formik.submitForm()
      handleSubmit({ deliveryAddress, values: formik.values })

      datadogRum.addAction('signup_checkout_click_place_order', {
        isFormikValid: formik.isValid,
        hasEmail: Boolean(email),
        hasDeliveryAddress: Boolean(deliveryAddress.get('postcode')),
      })
    }
  }

  return (
    <Box display={hide ? Display.None : Display.Block}>
      <FormikProvider value={formik}>
        <Form>
          <CheckoutFrame
            setHostedFieldsFrameLoaded={setHostedFieldsFrameLoaded}
            formValidationError={formValidationError}
          />
          <CheckoutBillingAddress />
          <ErrorMessage onLoginClick={onLoginClick} />
          <SubmitButton onClick={processSubmit} isDisabled={isFormSubmitting} />
        </Form>
      </FormikProvider>
    </Box>
  )
}
